import Axios from 'axios';
import { ClientAPI } from 'api';
import { User } from './userApi';
import { Contact } from './clientsApi';
import {
  Inspection,
  RoomFeedItem,
  RotatedPhotoDetailsInterface,
} from './inspectionsApi';
import { Organization } from './organizationApi';
import { z } from 'zod';
import { RecurringOptions } from 'components/ScheduledTasks/RecurringTasksOptions';
import base64ImageToBlob from 'util/base64ImageToBlob';
import { countryList } from 'util/countryAndStateUtil';
import qs from 'qs';
import { Branch } from './branchApi';
import { ActivityBucket } from 'components/Jobs/Filters/TimeSinceLastActiveAutocompleteForFilter';
import { buildSendEmailFormData } from 'components/Email/composePayload';

export enum StatusValue {
  NOT_STARTED = 'Not started',
  IN_PROGRESS = 'In progress',
  ON_HOLD = 'On hold',
  APPROVAL_PENDING = 'Approval pending',
  CANCELLED = 'Cancelled',
  CUSTOMER_CONTACTED = 'Customer contacted',
  INITIAL_REPORT_SUBMITTED = 'Initial report submitted',
  PROGRESS_REPORT_SUBMITTED = 'Progress report submitted',
  TO_BE_INVOICED = 'To be invoiced',
  INVOICED = 'Invoiced',
  COMPLETED = 'Completed',
}

/**
 * The statuses that count as active work. Mirrors the active job filter the
 * API counts with, so anything derived from it agrees with the ACTIVE JOBS
 * metric on the dashboard.
 */
export const ACTIVE_JOB_STATUSES = Object.values(StatusValue).filter(
  (status) =>
    status !== StatusValue.NOT_STARTED &&
    status !== StatusValue.COMPLETED &&
    status !== StatusValue.CANCELLED
);

export interface Job {
  id?: string;
  isPrivateJob: boolean;
  client?: ClientAPI.Client;
  clientId?: string;
  clientContact?: Contact;
  clientContactId?: string;
  organizationId: number;
  policyHolderId: string;
  purchaseOrder?: string;
  claimNumber?: string;
  insuranceCompany?: string;
  jobNo?: string;
  projectManagerId: string;
  projectManager: User;
  supervisorId: string;
  supervisor: User;
  jobTechnicians?: { user: User }[];
  description?: string;
  startDate?: string;
  createdOn: string;
  updatedOn: string;
  lastActivityAt: string | null;
  type: string;
  siteAddress: string;
  contactName: string;
  contactEmail?: string;
  contactNumber?: string;
  currentStatus: JobStatus;
  statuses?: JobStatus[];
  tasks?: ScheduledTask[];
  identifier?: string;
  overviewImage?: JobOverviewImage;
  createdBy?: User;
  state: string;
  country: countryList;
  branchId?: string;
  branch?: Branch;
  inspection?: Inspection;
  policyCoverType?: PolicyCoverTypes;
  limitedUserJobs?: {
    userId: string;
    jobId: string;
    organizationId: number;
    permittedOn: string;
    permittedByUserId: string;
  }[];
  equipmentCapVariables?: {
    realDateCount: number;
    cappedDateCount: number;
  } | null;
}

export interface JobOverviewImage {
  id?: string;
  organizationId: number;
  createdOn: string;
  updatedOn: string;
  name: string;
  mimeType: string;
  createdByUserId: string;
  gcsPath: string;
  updatedByUserId: string;
  createdByUser?: User;
  updatedByUser?: User;
}
export interface JobStatus {
  id?: string;
  value: StatusValue;
  notes: string;
  createdOn: string;
  createdByUserId: string;
  createdByUser?: User;
}

export interface taskAssignment {
  userId: string;
  user?: User;
  scheduledTaskId: string;
  scheduledTask?: ScheduledTask;
  createdOn: StereoPannerNode;
  createdByUserId: string;
  createdByUser?: User;
}

export const ScheduledTaskAttachmentDetailsSchema = z.object({
  id: z.string(),
  taskId: z.string(),
  organizationId: z.string(),
  jobId: z.string().optional(),
  name: z.string().optional(),
  mimeType: z.string().optional(),
  createdOn: z.string().optional(),
  gcsPath: z.string().optional(),
});

export type ScheduledTaskAttachmentDetails = z.infer<
  typeof ScheduledTaskAttachmentDetailsSchema
>;

export interface ScheduledTask {
  id?: string;
  job?: Job;
  organization?: Organization;
  jobId?: string;
  name?: string;
  description?: string;
  updatedOn?: string;
  updatedByUser?: User;
  createdByUser?: User;
  createdByUserId?: string;
  taskAssignments?: taskAssignment[];
  assignedUserIds?: string[]; // Only on client side (POST data)
  start: string;
  end: string;
  attachments?: File[] | ScheduledTaskAttachmentDetails[];
  recurringData?: RecurringOptions;
  isRecurring?: boolean;
}
export interface Note {
  id?: string;
  jobId: string;
  text: string;
  isPublic: boolean;
  updatedOn: string;
  updatedByUserId: string;
  updatedByUser: User;
}

export interface SearchParams {
  companyName?: string;
  status?: string;
  pmName?: string;
  technicianName?: string;
  q?: string; // search
}

export type JobSortField =
  | 'jobNo'
  | 'createdOn'
  | 'lastAttendance'
  | 'claimNumber'
  | 'workOrderNo';

export type JobSortOrder = 'ASC' | 'DESC';

export interface JobSortOption {
  field: JobSortField;
  order: JobSortOrder;
}

export type JobListFilters = {
  clientId?: string;
  clientLegalName?: string[];
  projectManagerId?: string;
  projectCoordinatorId?: string[];
  technicianId?: string;
  state?: string[];
  branchId?: string[];
  currentStatus?: StatusValue[];
  searchKeyword?: string;
  jobCategory?: string;
  selectedJobCategory?: string;
  myJobs?: boolean;
  projectSupervisorId?: string[];
  timeSinceLastActive?: ActivityBucket[];
  lastActivityFrom?: string | null;
  lastActivityTo?: string | null;
  sortBy?: JobSortOption | null;
};

export enum JobTableType {
  ALL = 'All_Jobs',
  CLOSED = 'Closed_Jobs',
  OPEN = 'Open_Jobs',
  MY_JOBS = 'My_Jobs',
}

export enum PolicyCoverTypes {
  BUILDING = 'Building',
  CONTENTS = 'Contents',
  BUILDING_AND_CONTENTS = 'Building and Contents',
}

export async function listAllJobs({
  filters,
  page,
  pageSize,
}: {
  filters?: JobListFilters;
  page: number;
  pageSize: number;
}): Promise<{
  jobs: Job[];
  identifier: string;
  page: number;
  pageSize: number;
  totalJobCount: number;
  activeColumns: string[];
  serverClockOffsetMs: number | null;
}> {
  const params: any = {
    jobFilters: filters ?? null,
    page,
    pageSize,
  };
  const res = await Axios.get(`/api/v2/jobs`, {
    params,
    paramsSerializer: (params) =>
      qs.stringify(params, { arrayFormat: 'repeat' }),
  });
  const serverDateHeader = res.headers?.date;
  const serverTimeMs = serverDateHeader
    ? new Date(serverDateHeader).getTime()
    : NaN;
  return {
    ...res.data,
    serverClockOffsetMs: isNaN(serverTimeMs) ? null : serverTimeMs - Date.now(),
  };
}

export async function listAllJobSummaries(): Promise<
  Pick<Job, 'id' | 'siteAddress' | 'claimNumber' | 'jobNo'>[]
> {
  const res = await Axios.get('/api/v1/jobs/list-summary');

  return res.data;
}

export async function getJobById(id: string): Promise<Job> {
  const res = await Axios.get(`/api/v1/jobs/${id}`);
  return res.data;
}

export interface CreateUpdateJob extends Job {
  technicianIds?: string[];
}

export async function createJob(data: CreateUpdateJob) {
  const res = await Axios.post('/api/v2/jobs', data);
  return res.data;
}

export async function updateJob(id: string, data: Partial<CreateUpdateJob>) {
  const res = await Axios.put(`/api/v2/jobs/${id}`, data);
  return res.data;
}

export async function deleteJob(id: string) {
  await Axios.delete(`/api/v1/jobs/${id}`);
}

export async function updateJobStatus({
  id,
  data,
}: {
  id: string;
  data: JobStatus;
}) {
  const res = await Axios.post(`/api/v1/jobs/${id}/status`, data);
  return res.data;
}

export async function deleteJobStatus({ jobId, statusId }) {
  const res = await Axios.delete(`/api/v1/jobs/${jobId}/status/${statusId}`);
  return res.data;
}

export async function listNotes(jobId: string): Promise<Note[]> {
  const res = await Axios.get(`/api/v1/notes/${jobId}`);
  return res.data;
}

export async function createNote({
  jobId,
  text,
  isPublic,
}: {
  jobId: string;
  text: string;
  isPublic?: boolean;
}) {
  const res = await Axios.post(`/api/v1/notes/${jobId}`, { text, isPublic });
  return res.data;
}

export async function updateNote({
  jobId,
  noteId,
  text,
  isPublic,
}: {
  jobId: string;
  noteId: string;
  text?: string;
  isPublic?: boolean;
}) {
  const res = await Axios.put(`/api/v1/notes/${jobId}/${noteId}`, {
    text,
    isPublic,
  });
  return res.data;
}

export async function deleteNote({ jobId, noteId }) {
  const res = await Axios.delete(`/api/v1/notes/${jobId}/${noteId}`);
  return res.data;
}

export async function listJobSMS(jobId: string) {
  const res = await Axios.get(`/api/v1/sms/${jobId}`);
  return res.data;
}

export async function sendSMS({ jobId, from, recipientNumber, text }) {
  const res = await Axios.post(`/api/v1/sms`, {
    recipientNumber,
    text,
    jobId,
    from,
  });
  return res.data;
}

export async function listJobEmails(jobId: string) {
  const res = await Axios.get(`/api/v1/email/${jobId}`);
  return res.data;
}

export async function getAttachmentPreviewLink(
  jobId: string,
  emailId: string,
  fileId: string
) {
  const res = await Axios.get(
    `/api/v1/email/${emailId}/job/${jobId}/${fileId}/url`
  );
  return res.data;
}

export type CorrespondenceDirection = 'outbound' | 'inbound';

export interface CorrespondenceAttachment {
  id: string;
  name: string;
  mimeType: string;
  fileByteSize: number;
}

/** Just enough of the sender to render an avatar and spot our own mail. */
export interface CorrespondenceUser {
  id: string;
  firstName: string;
  lastName: string;
  dp?: string;
}

/**
 * One message in a job's two-way email history. The API normalizes both
 * directions to `bodyHtml` — outbound Draft.js bodies are converted
 * server-side — so this is never Draft.js raw JSON and must not be passed to
 * DraftjsPreviewBody.
 */
export interface CorrespondenceItem {
  id: string;
  direction: CorrespondenceDirection;
  occurredAt: string;
  subject?: string;
  from?: string;
  /** Outbound only — received mail has no Restorize user behind it. */
  fromUser?: CorrespondenceUser;
  to: string[];
  cc: string[];
  /** Outbound only — a recipient never sees the inbound Bcc list. */
  bcc: string[];
  bodyHtml: string;
  status: string;
  attachments: CorrespondenceAttachment[];
  /** RFC Message-ID, used to thread replies together. */
  messageId?: string;
  /**
   * Message-ID this one replies to. Set on received mail from its headers, and
   * on mail we sent with Reply. Absent on a fresh compose, and on anything sent
   * before reply threading existed — those thread by subject instead, see
   * groupIntoThreads.
   */
  inReplyTo?: string;
  /** Oldest-first chain of ancestor Message-IDs. */
  references?: string[];
  /**
   * Inbound only. When the message was marked read, or absent if unread.
   * Read state is shared across the organization, not per user: one person
   * opening a reply marks it handled for the whole team.
   */
  readAt?: string;
}

/** Merged sent + received email history for a job, newest first. */
export async function getJobCorrespondence(
  jobId: string
): Promise<CorrespondenceItem[]> {
  const res = await Axios.get(`/api/v1/email/${jobId}/correspondence`);
  return res.data;
}

/**
 * Marks received messages read or unread. Only inbound ids are meaningful —
 * mail we sent has no read state.
 */
export async function setCorrespondenceReadState({
  jobId,
  ids,
  read,
}: {
  jobId: string;
  ids: string[];
  read: boolean;
}): Promise<{ success: boolean; updated: number }> {
  const res = await Axios.patch(
    `/api/v1/email/${jobId}/correspondence/read`,
    { ids, read }
  );
  return res.data;
}

export interface UnreadEmailSummary {
  unreadCount: number;
  latestUnreadAt?: string;
  latestFrom?: string;
  latestSubject?: string;
}

/**
 * Unread count plus a one-line preview, for the job's Email tab badge and the
 * overview card. Separate from the correspondence payload so a badge does not
 * require loading every message on the job.
 */
export async function getJobUnreadEmailSummary(
  jobId: string
): Promise<UnreadEmailSummary> {
  const res = await Axios.get(
    `/api/v1/email/${jobId}/correspondence/unread-summary`
  );
  return res.data;
}

/** Subject and body a new email on a job should open with. */
export interface ComposeDefaults {
  subject: string;
  bodyHtml: string;
}

/**
 * The organization's email template, rendered for this job.
 *
 * Resolved server-side rather than assembled here, so that when organizations
 * author their own templates — with their own placeholders for the job address,
 * the sender's name and the rest — nothing in the compose form changes.
 */
export async function getJobComposeDefaults(
  jobId: string
): Promise<ComposeDefaults> {
  const res = await Axios.get(`/api/v1/email/${jobId}/compose-defaults`);
  return res.data;
}

/**
 * Signed URL for an attachment on a RECEIVED email. The route shape differs
 * from the outbound equivalent (`getAttachmentPreviewLink`), which is keyed on
 * job + email.
 */
export async function getInboundAttachmentPreviewLink(
  inboundEmailId: string,
  attachmentId: string
) {
  const res = await Axios.get(
    `/api/v1/inbound-email/${inboundEmailId}/attachment/${attachmentId}/url`
  );
  return res.data;
}

export async function sendJobEmail({ jobId, data }) {
  // Field-by-field encoding lives in composePayload.ts, where it is unit
  // tested — every value ends up a string in FormData, so a field encoded the
  // wrong way typechecks fine and fails only against the live API.
  const formData = buildSendEmailFormData(data);

  const res = await Axios.post(`/api/v1/email/${jobId}`, formData, {
    headers: { 'content-type': 'multipart/form-data' },
  });

  return res.data;
}

export interface PaginatedInventoryItems {
  items: RoomFeedItem[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listJobInventoryByRoom(
  jobId: string,
  roomId: string,
  page?: number,
  pageSize?: number
): Promise<PaginatedInventoryItems> {
  const res = await Axios.get(
    `/api/v1/jobs/${jobId}/room/${roomId}/inventory`,
    {
      params: { page, pageSize },
    }
  );
  return res.data;
}

export async function getJobStatus(jobId: string) {
  const res = await Axios.get(`/api/v2/jobs/status/${jobId}`);
  return res.data;
}

export async function uploadJobOverviewImage({
  id,
  image,
}: {
  id: string;
  image: string;
}) {
  const formData = new FormData();
  formData.append('jobOverviewImage', base64ImageToBlob(image));
  const res = await Axios.post(`/api/v1/jobs/${id}/overViewImage`, formData, {
    headers: { 'content-type': 'multipart/form-data' },
  });
  return res.data;
}

export async function updateJobOverviewImage({
  id,
  newImage,
  rotatedPhotoDetails,
}: {
  id: string;
  newImage?: string;
  rotatedPhotoDetails?: RotatedPhotoDetailsInterface;
}) {
  const formData = new FormData();
  if (newImage) {
    formData.append('jobOverviewImage', base64ImageToBlob(newImage));
  }
  if (rotatedPhotoDetails) {
    formData.append('rotatedPhotoDetails', JSON.stringify(rotatedPhotoDetails));
  }
  const res = await Axios.put(`/api/v1/jobs/${id}/jobOverviewImage`, formData, {
    headers: { 'content-type': 'multipart/form-data' },
  });
  return res.data;
}

export async function deleteJobOverviewImage(id: string) {
  await Axios.delete(`/api/v1/jobs/${id}/jobOverviewImage`);
}

export async function getJobStateTimezone(jobId: string) {
  const res = await Axios.get(`/api/v2/jobs/${jobId}/timezone`);
  return res.data;
}

interface IJobCompletionSummary {
  id: string;
  jobId: string;
  organizationId: string;
  summary: string;
  createdOn: string;
  createdByUserId: string;
  createdByUser: User;
  updatedOn: string;
  updatedByUserId: string;
  updatedByUser: User;
}

export async function saveJobCompletionSummary({
  jobId,
  summary,
}: {
  jobId: string;
  summary: string;
}) {
  const res = await Axios.post(`/api/v1/job-completion-summary/${jobId}`, {
    summary,
  });
  return res.data;
}

export async function getJobCompletionSummary(
  jobId: string
): Promise<IJobCompletionSummary> {
  const res = await Axios.get(`/api/v1/job-completion-summary/${jobId}`);
  return res.data;
}

export async function editPermitJobToLimitedUsers({
  limitedUserIds,
  jobId,
}: {
  limitedUserIds: string[];
  jobId: string;
}) {
  const res = await Axios.post(
    `/api/v1/jobs/${jobId}/edit-permit-limited-users`,
    {
      limitedUserIds,
    }
  );

  return res.data;
}
